import React from "react";
import { Box, Grid, HStack, Select, Stack, Switch, Text, createListCollection } from "@chakra-ui/react";
import { DashboardDeleteButton, DashboardInput, DashboardSelectTrigger, DashboardSelectContent, DashboardSelectItem, DashboardTruncatedText } from "components/utils/FormControls";
import { Button } from "components/ui/Button";
import { MdPayments } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import { HiCheck, HiX } from "react-icons/hi";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import { getAvatarHexColor, getAvatarTint } from "utils/avatar";
import { asArray, isPrefixedTempId, makePrefixedTempId } from "../shared/helpers";
import {
  useCreateDashboardActividadGasto,
  useDashboardActividadGastos,
  useDashboardGastosCategoriasCatalog,
  useDeleteDashboardActividadGasto,
  usePatchDashboardActividadGasto,
} from "features/dashboard/actividades/hooks";

const TMP_PREFIX = "gto_";

function toNumberOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 }).format(n);
}

function gastoTone(seed) {
  const solid = getAvatarHexColor(seed);
  return {
    solid,
    bg: getAvatarTint(seed, 12),
  };
}

function CategoriaSelect({ collection, value, onChange }) {
  return (
    <Select.Root
      collection={collection}
      size="sm"
      value={value ? [value] : []}
      onValueChange={(e) => onChange(e.value?.[0] ?? "")}
      positioning={{ strategy: "fixed", hideWhenDetached: true }}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <DashboardSelectTrigger>
          <Select.ValueText placeholder="Selecciona categoria" />
        </DashboardSelectTrigger>
        <Select.IndicatorGroup>
          <Select.Indicator color="dashboard.accent.solid" />
        </Select.IndicatorGroup>
      </Select.Control>
      <Select.Positioner>
        <DashboardSelectContent>
          {collection.items.map((item) => (
            <DashboardSelectItem key={item.value} item={item}>
              {item.label}
              <Select.ItemIndicator />
            </DashboardSelectItem>
          ))}
        </DashboardSelectContent>
      </Select.Positioner>
    </Select.Root>
  );
}

export const PosteriorGastosCard = ({ actividad, actividadId, patch }) => {
  const dialog = useDialogForm({ gastosRequeridos: true, rows: [] });
  const [localError, setLocalError] = React.useState(null);

  const gastosQ = useDashboardActividadGastos(actividadId, !!actividadId);
  const categoriasQ = useDashboardGastosCategoriasCatalog(true);

  const createGasto = useCreateDashboardActividadGasto();
  const patchGasto = usePatchDashboardActividadGasto();
  const deleteGasto = useDeleteDashboardActividadGasto();

  const saving = createGasto.isPending || patchGasto.isPending || deleteGasto.isPending || patch?.isPending;
  const apiError =
    (createGasto.isError && String(createGasto.error?.message || createGasto.error)) ||
    (patchGasto.isError && String(patchGasto.error?.message || patchGasto.error)) ||
    (deleteGasto.isError && String(deleteGasto.error?.message || deleteGasto.error)) ||
    (patch?.isError && String(patch.error?.message || patch.error)) ||
    null;
  const errorText = localError || apiError;
  const gastosRequeridos = actividad?.gastosRequeridos !== false;

  const dbRows = asArray(gastosQ.data).map((row) => ({
    id: String(row.id),
    concepto: row.concepto || "",
    categoria: row.categoria || "",
    cantidad: Number(row.cantidad ?? 0),
  }));

  const categorias = asArray(categoriasQ.data);
  const categoriaItems = React.useMemo(
    () => categorias.map((c) => ({ value: String(c.id), label: c.nombre || "Categoria" })),
    [categorias]
  );
  const categoriaCollection = React.useMemo(() => createListCollection({ items: categoriaItems }), [categoriaItems]);
  const categoriaById = React.useMemo(() => new Map(categoriaItems.map((c) => [c.value, c.label])), [categoriaItems]);

  function openDialog() {
    setLocalError(null);
    dialog.openWith({ gastosRequeridos, rows: dbRows });
  }

  function setGastosRequeridos(value) {
    dialog.setField("gastosRequeridos", value);
    if (!value) dialog.setField("rows", []);
  }

  function addRow() {
    dialog.setField("rows", [
      ...asArray(dialog.form.rows),
      { id: makePrefixedTempId(TMP_PREFIX), concepto: "", categoria: "", cantidad: "" },
    ]);
  }

  function removeRow(rowId) {
    dialog.setField(
      "rows",
      asArray(dialog.form.rows).filter((r) => String(r.id) !== String(rowId))
    );
  }

  function patchRow(rowId, patch) {
    dialog.setField(
      "rows",
      asArray(dialog.form.rows).map((r) => (String(r.id) === String(rowId) ? { ...r, ...patch } : r))
    );
  }

  async function save() {
    setLocalError(null);

    const needsGastos = dialog.form.gastosRequeridos !== false;
    if (!needsGastos) {
      try {
        for (const row of dbRows) {
          await deleteGasto.mutateAsync({ actividadId, gastoId: row.id });
        }

        await patch.mutateAsync({
          actividadId,
          patch: { gastosRequeridos: false },
        });

        dialog.close();
        return;
      } catch (e) {
        setLocalError(String(e?.message || e));
        throw e;
      }
    }

    const prevById = new Map(dbRows.map((r) => [r.id, r]));
    const nextRows = asArray(dialog.form.rows).map((r) => ({
      id: String(r.id),
      concepto: String(r.concepto ?? "").trim(),
      categoria: String(r.categoria ?? "").trim(),
      cantidad: toNumberOrNull(r.cantidad),
    }));

    const invalidRow = nextRows.find((r) => !r.concepto || !r.categoria || r.cantidad == null || r.cantidad < 0);
    if (invalidRow) {
      setLocalError("Cada gasto debe tener concepto, categoria y cantidad valida (>= 0).");
      return;
    }

    const nextIds = new Set(nextRows.filter((r) => !isPrefixedTempId(r.id, TMP_PREFIX)).map((r) => r.id));
    const toDelete = dbRows.filter((r) => !nextIds.has(r.id));
    const toCreate = nextRows.filter((r) => isPrefixedTempId(r.id, TMP_PREFIX) || !prevById.has(r.id));
    const toPatch = nextRows.filter((r) => !isPrefixedTempId(r.id, TMP_PREFIX) && prevById.has(r.id)).filter((r) => {
      const prev = prevById.get(r.id);
      return prev.concepto !== r.concepto || prev.categoria !== r.categoria || Number(prev.cantidad) !== Number(r.cantidad);
    });

    try {
      for (const row of toDelete) {
        await deleteGasto.mutateAsync({ actividadId, gastoId: row.id });
      }

      for (const row of toCreate) {
        await createGasto.mutateAsync({
          actividadId,
          payload: { concepto: row.concepto, categoria: row.categoria, cantidad: row.cantidad },
        });
      }

      for (const row of toPatch) {
        await patchGasto.mutateAsync({
          actividadId,
          gastoId: row.id,
          patch: { concepto: row.concepto, categoria: row.categoria, cantidad: row.cantidad },
        });
      }

      if (!gastosRequeridos) {
        await patch.mutateAsync({
          actividadId,
          patch: { gastosRequeridos: true },
        });
      }

      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  const total = React.useMemo(() => dbRows.reduce((acc, row) => acc + Number(row.cantidad || 0), 0), [dbRows]);

  return (
    <>
      <DataCard
        title="Gastos"
        variant="green"
        headerIcon={<MdPayments size="24px" />}
        onEdit={openDialog}
        editAriaLabel="Editar gastos"
        isIncomplete={gastosRequeridos && !gastosQ.isLoading && dbRows.length === 0}
      >
        {gastosQ.isLoading ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Cargando gastos...</Text>
        ) : !gastosRequeridos ? (
          <Text fontWeight="700" color="dashboard.fg.default" fontSize="sm">No se requieren gastos.</Text>
        ) : dbRows.length === 0 ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Sin gastos registrados.</Text>
        ) : (
          <Stack gap="2.5" w="full" minW="0">
            <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
              {dbRows.map((row) => {
                const tone = gastoTone(row.id || row.concepto || row.categoria);
                const categoriaLabel = categoriaById.get(row.categoria) || "Categoria";
                return (
                  <Box key={row.id} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" overflow="hidden">
                    <Stack gap="1" minW="0">
                      <HStack gap="2" minW="0" justify="space-between" align="start">
                        <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                          {row.concepto}
                        </DashboardTruncatedText>

                        <Box rounded="pill" px="3" py="1.5" bg={tone.solid} color="white" fontSize="sm" fontWeight="900" lineHeight="1" flexShrink={0}>
                          {fmtMoney(row.cantidad)}
                        </Box>
                      </HStack>

                      <Text color="dashboard.fg.muted" fontSize="xs" fontWeight="700" whiteSpace="normal" overflowWrap="anywhere">
                        {categoriaLabel}
                      </Text>
                    </Stack>
                  </Box>
                );
              })}
            </Grid>
            <HStack justify="space-between" pt="2.5" borderTopWidth="1px" borderColor="dashboard.border.muted">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Total</Text>
              <Box rounded="pill" px="3" py="1.5" bg="dashboard.bg.surfaceMuted" color="dashboard.fg.default" fontWeight="900" fontSize="sm">
                {fmtMoney(total)}
              </Box>
            </HStack>
          </Stack>
        )}
      </DataCard>

      <FormDialog
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title="Editar Gastos"
        errorText={errorText}
        saving={saving}
        onCancel={dialog.close}
        onSave={save}
      >
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Gastos</Text>
            <Text color="dashboard.fg.muted" fontSize="sm">Se necesitan gastos para esta actividad?</Text>
          </Box>
          <Switch.Root size="lg" checked={dialog.form.gastosRequeridos !== false} onCheckedChange={(e) => setGastosRequeridos(!!e.checked)}>
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb>
                <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                  <HiCheck />
                </Switch.ThumbIndicator>
              </Switch.Thumb>
            </Switch.Control>
          </Switch.Root>
        </HStack>

        {dialog.form.gastosRequeridos !== false && (
          <>
        <HStack justify="space-between">
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Gastos de la actividad</Text>
          <Button variant="surface" color="red" size="sm" onClick={addRow}>
            <HStack gap="2">
              <FiPlus />
              <Text>Agregar</Text>
            </HStack>
          </Button>
        </HStack>

        <Grid templateColumns="2fr 1.5fr 1fr auto" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Concepto</Text>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Categoria</Text>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Cantidad</Text>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted"></Text>
        </Grid>

        <Stack gap="2">
          {asArray(dialog.form.rows).length === 0 ? (
            <Text color="dashboard.fg.muted">Sin gastos.</Text>
          ) : (
            asArray(dialog.form.rows).map((row) => (
              <Grid key={row.id} templateColumns="2fr 1.5fr 1fr auto" gap="3" px="2" py="2" alignItems="center">
                <DashboardInput
                  placeholder="Ej. Compra de agua"
                  value={String(row.concepto ?? "")}
                  onChange={(e) => patchRow(row.id, { concepto: e.target.value })}
                />
                <CategoriaSelect
                  collection={categoriaCollection}
                  value={row.categoria || ""}
                  onChange={(value) => patchRow(row.id, { categoria: value })}
                />
                <DashboardInput
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={String(row.cantidad ?? "")}
                  onChange={(e) => patchRow(row.id, { cantidad: e.target.value })}
                />
                <DashboardDeleteButton aria-label="Eliminar gasto" onClick={() => removeRow(row.id)} />
              </Grid>
            ))
          )}
        </Stack>
          </>
        )}
      </FormDialog>
    </>
  );
};
